import * as React from 'react';
import Popper, { PopperProps } from '@mui/material/Popper';

type Props = {
    component: React.ReactNode;
    children: React.ReactNode;
    popperProps?: PopperProps;
};

/** This component is a wrapper that manages the "display children component on hover" behavior.
 * This is useful when we want to show a sub menu without requiring user to click.
 * It allows faster interactions, but requires some hover in/out tricky management.
 * We hide the hover management complexity here.
 *
 * Args:
 * -----
 * The `props.children` component is open when we hover the `props.component`.
 * We use internally the material ui `Popper` component, extra props can be given with
 * `props.popperPros.` It is useful to chose where to open the children component.
 * See the defaultProps for an example.
 */
function HoverPopper({
    component,
    children,
    popperProps = { open: false, placement: 'right-end' },
}: Props): React.ReactElement {
    const [anchorEl, setAnchor] = React.useState<
        (EventTarget & HTMLDivElement) | null
    >(null);
    const [isOverMenu, setOverMenu] = React.useState(false);
    const [isOverComponent, setOverComponent] = React.useState(false);
    const [lastEnterHoverTime, setEnterHoverTime] = React.useState(
        new Date().getTime(),
    );

    const isPopperOpen = isOverMenu || isOverComponent;

    const closeMenu = () => {
        setOverComponent(false);
        setOverMenu(false);
    };
    const toggleMenu = () => {
        if (!isPopperOpen) setOverComponent(true);
        else closeMenu();
    };

    return (
        <>
            <div
                onMouseEnter={(event) => {
                    setAnchor(event.currentTarget);
                    setOverComponent(true);
                    setEnterHoverTime(new Date().getTime());
                }}
                onClick={(event) => {
                    setAnchor(event.currentTarget);
                    // we don't want to toggle the menu when the user just hovered the button
                    // because it means she is trying to open it and clicking on it would close it.
                    const ignoreClick =
                        new Date().getTime() - lastEnterHoverTime < 1000; // 1s
                    if (!ignoreClick) toggleMenu();
                }}
                onMouseLeave={() => {
                    // We use a timeout because we don't want to hide the menu
                    // when the user is leaving this component "diagonally" to get
                    // to the menu without always staying above the component.
                    setTimeout(() => setOverComponent(false), 50);
                }}
                role="button"
                tabIndex={0}
            >
                {component}
            </div>
            <Popper
                {...popperProps}
                open={isPopperOpen}
                anchorEl={anchorEl}
                onMouseEnter={() => setOverMenu(true)}
                onMouseLeave={() => setOverMenu(false)}
                onClick={closeMenu}
                // added to prevent a position bug on Chrome. Cause is unknown, it's been noticed days after AgGrid and React upgrades.
                modifiers={[
                    {
                        name: 'flip',
                        enabled: false,
                    },
                ]}
            >
                {children}
            </Popper>
        </>
    );
}

export default HoverPopper;
